// Ativar/desativar — o mesmo fluxo na lista e no detalhe. Ativar é direto
// (devolve a regra a novos vínculos, sem efeito colateral); desativar SEMPRE
// passa pelo diálogo de confirmação.
import { useCallback, useState } from "react"

import {
  useActivatePaymentConfigurationMutation,
  useDeactivatePaymentConfigurationMutation,
} from "@/services/payment-configurations"
import type { PaymentConfiguration } from "@/types/payment-configurations"

export const usePaymentConfigurationToggle = () => {
  const activateMutation =
    useActivatePaymentConfigurationMutation()
  const deactivateMutation =
    useDeactivatePaymentConfigurationMutation()

  const [pendingDeactivate, setPendingDeactivate] =
    useState<PaymentConfiguration | null>(null)

  const togglingId =
    (activateMutation.isPending
      ? activateMutation.variables
      : undefined) ??
    (deactivateMutation.isPending
      ? deactivateMutation.variables
      : undefined) ??
    null

  // `mutate` é estável entre renders — o card memoizado não re-renderiza à toa.
  const { mutate: activate } = activateMutation
  const { mutate: deactivate } = deactivateMutation

  const handleActivate = useCallback(
    (configuration: PaymentConfiguration) =>
      activate(configuration.id),
    [activate]
  )

  const handleDeactivate = useCallback(
    (configuration: PaymentConfiguration) =>
      setPendingDeactivate(configuration),
    []
  )

  const handleToggle = useCallback(
    (configuration: PaymentConfiguration) =>
      configuration.active
        ? handleDeactivate(configuration)
        : handleActivate(configuration),
    [handleActivate, handleDeactivate]
  )

  const confirmDeactivate = useCallback(
    (configuration: PaymentConfiguration) => {
      deactivate(configuration.id)
      setPendingDeactivate(null)
    },
    [deactivate]
  )

  const closeDeactivate = useCallback(
    () => setPendingDeactivate(null),
    []
  )

  return {
    togglingId,
    isToggling: togglingId !== null,
    handleActivate,
    handleDeactivate,
    handleToggle,
    pendingDeactivate,
    confirmDeactivate,
    closeDeactivate,
  }
}
